import {
  onLCP,
  onINP,
  onCLS,
  onFCP,
  type LCPMetricWithAttribution,
  type INPMetricWithAttribution,
  type CLSMetricWithAttribution,
  type FCPMetricWithAttribution,
} from 'web-vitals/attribution';
import enviro from 'enviro';
import { captureException } from '../ravenUtils';

import CartographerReporter, {
  THIRTY_SECONDS,
  RouteInfo,
  NavigationAction,
} from './CartographerReporter';
import { ReporterAction } from '../types';
import { ReporterOptions } from './BaseReporter';
import { onVisibilityHidden } from '../visibility';
import {
  getElementSelector,
  getNearestComponentName,
} from '../elementSelector';
import performanceNow from '../../vendor/performanceNow';
import PageStateTracker from '../PageStateTracker';
import PerformanceEventBus from '../PerformanceEventBus';
import { isInIframe } from '../browserAccessors';
import { PerformanceLongAnimationFrameTiming } from '../performanceApi';

const WEB_VITALS_ADAPTER_ENABLED = true;

export function isWebVitalsAdapterEnabled(): boolean {
  if (typeof Array.prototype.at !== 'function') return false;
  return (
    WEB_VITALS_ADAPTER_ENABLED ||
    enviro.debug('react-rhumb-web-vitals-adapter') === 'true'
  );
}

export default class WebVitalsAdapter extends CartographerReporter {
  private static readonly WINDOW_KEY = '__rhumb_web_vitals_adapter__';

  public static getInstance(options?: ReporterOptions): WebVitalsAdapter {
    const w = window as any;
    if (!w[WebVitalsAdapter.WINDOW_KEY]) {
      w[WebVitalsAdapter.WINDOW_KEY] = new WebVitalsAdapter(options);
    }
    return w[WebVitalsAdapter.WINDOW_KEY];
  }

  public static resetInstance(): void {
    (window as any)[WebVitalsAdapter.WINDOW_KEY] = undefined;
  }

  lastRouteInfo: RouteInfo | undefined;
  lastStatus: NavigationAction['status'] | undefined;
  lastFailureType: NavigationAction['failureType'] | undefined;
  stopped: boolean;
  flushQueueTimeout: number | undefined;

  getPageState() {
    const pageStateTracker = PageStateTracker.getInstance();
    const pageStateSummary = pageStateTracker.getStateSummary();
    const currentSnapshot = pageStateTracker.getSnapshot();

    return {
      currentOnline: currentSnapshot.online,
      currentHidden: currentSnapshot.hidden,
      wasEverHidden: pageStateSummary.wasEverHidden,
      wasEverOffline: pageStateSummary.wasEverOffline,
    };
  }

  private debounceFlush = () => {
    clearTimeout(this.flushQueueTimeout);
    this.flushQueueTimeout = setTimeout(() => {
      this.flushPerformanceQueue();
    }, THIRTY_SECONDS);
  };

  private handleLCP = (metric: LCPMetricWithAttribution) => {
    try {
      if (metric.navigationType === 'soft-navigation') {
        this.handleSoftNavLCP(metric);
      } else {
        this.handleHardNavLCP(metric);
      }
    } catch (e) {
      captureException(e instanceof Error ? e : new Error(String(e)));
    }
  };

  private handleSoftNavLCP(metric: LCPMetricWithAttribution): void {
    const { attribution } = metric;
    const lcpEntry = attribution.lcpEntry;
    const element = lcpEntry?.element
      ? getElementSelector(lcpEntry.element)
      : attribution.target ?? '';
    const componentName = getNearestComponentName(lcpEntry?.element ?? null);
    const fcpEntry = performance
      .getEntriesByType?.('paint')
      ?.find((e) => e.name === 'first-contentful-paint');
    const fcpTime = fcpEntry?.startTime ?? 0;
    const isEmbedded = isInIframe();

    const elementRenderTimestamp =
      lcpEntry?.renderTime || lcpEntry?.loadTime || metric.value;
    this.pushPerformanceAction(this.lastRouteInfo, 'web-vitals-soft-nav-lcp', {
      size: lcpEntry?.size ?? 0,
      value: metric.value,
      elementRenderTimestamp,
      elementLoadTimestamp: lcpEntry?.loadTime,
      startTime: metric.value,
      renderTime: elementRenderTimestamp,
      loadTime: lcpEntry?.loadTime,
      elementRenderDelay: attribution.elementRenderDelay ?? 0,
      id: metric.id,
      url: lcpEntry?.url,
      element,
      componentName,
      isEmbedded,
      fcpTime,
      navigationId: metric.navigationId,
      elapsedMs: performanceNow(),
      pageState: this.getPageState(),
      webVitalsSource: true,
    });
    this.debounceFlush();
  }

  private handleHardNavLCP(metric: LCPMetricWithAttribution): void {
    const { attribution } = metric;
    const lcpEntry = attribution.lcpEntry;
    const element = lcpEntry?.element
      ? getElementSelector(lcpEntry.element)
      : attribution.target ?? '';
    const componentName = getNearestComponentName(lcpEntry?.element ?? null);
    const fcpEntry = performance
      .getEntriesByType?.('paint')
      ?.find((e) => e.name === 'first-contentful-paint');
    const fcpTime = fcpEntry?.startTime ?? 0;
    const isEmbedded = isInIframe();

    const entryJson = lcpEntry ? lcpEntry.toJSON() : null;
    const rawRenderTime =
      entryJson?.renderTime || entryJson?.loadTime || metric.value;
    this.pushPerformanceAction(this.lastRouteInfo, 'largest-contentful-paint', {
      entryType: entryJson?.entryType ?? 'largest-contentful-paint',
      size: entryJson?.size ?? 0,
      startTime: entryJson?.startTime ?? metric.value,
      renderTime: rawRenderTime,
      loadTime: entryJson?.loadTime ?? 0,
      id: entryJson?.id ?? '',
      url: entryJson?.url ?? attribution.url ?? '',
      element,
      componentName,
      isEmbedded,
      fcpTime,
      elementRenderDelay: Math.max(rawRenderTime - fcpTime, 0),
      isReload: metric.navigationType === 'reload',
      status: this.lastStatus,
      failureType: this.lastFailureType,
      elapsedMs: performanceNow(),
      pageState: this.getPageState(),
      webVitalsSource: true,
    });
    this.debounceFlush();
  }

  private handleINP = (metric: INPMetricWithAttribution) => {
    try {
      const { attribution } = metric;
      const targetEl = metric.entries?.[0]?.target as Element | undefined;
      const testId =
        targetEl && targetEl.hasAttribute?.('data-test-id')
          ? targetEl.getAttribute('data-test-id') ?? ''
          : '';
      const tagName = targetEl
        ? getElementSelector(targetEl)
        : attribution.interactionTarget ?? '';
      const componentName = getNearestComponentName(targetEl ?? null);
      const interactionId =
        (metric.entries?.[0] as PerformanceEventTiming | undefined)
          ?.interactionId ?? 0;
      this.pushPerformanceAction(this.lastRouteInfo, 'event', {
        interactionId,
        interactionNames: attribution.interactionType ?? '',
        latency: metric.value,
        elapsedMs: performanceNow(),
        testId,
        tagName,
        componentName,
        pageState: this.getPageState(),
        webVitalsSource: true,
      });
      this.debounceFlush();
    } catch (e) {
      captureException(e instanceof Error ? e : new Error(String(e)));
    }
  };

  private handleCLS = (metric: CLSMetricWithAttribution) => {
    try {
      const { attribution } = metric;
      const largestShift = attribution.largestShiftEntry;
      const shiftSource = attribution.largestShiftSource;
      const largestShiftNode = shiftSource?.node ?? null;
      const componentName = getNearestComponentName(largestShiftNode);
      const largestShiftSource = shiftSource
        ? {
            previousRect: shiftSource.previousRect.toJSON(),
            currentRect: shiftSource.currentRect.toJSON(),
          }
        : undefined;

      let value = metric.value;
      const isEmbedded = isInIframe();

      if (isEmbedded) {
        const iframeArea = window.innerWidth * window.innerHeight;
        const browserArea = window.outerWidth * window.outerHeight;
        if (browserArea > 0 && iframeArea > 0 && iframeArea < browserArea) {
          value = metric.value * (iframeArea / browserArea);
        }
      }

      this.pushPerformanceAction(this.lastRouteInfo, 'layout-shift', {
        entryType: 'layout-shift',
        value,
        isEmbedded,
        startTime: largestShift?.startTime ?? 0,
        hadRecentInput: largestShift?.hadRecentInput ?? false,
        largestShiftTarget: attribution.largestShiftTarget ?? '',
        largestShiftSource,
        componentName,
        elapsedMs: performanceNow(),
        pageState: this.getPageState(),
        webVitalsSource: true,
      });
      this.debounceFlush();
    } catch (e) {
      captureException(e instanceof Error ? e : new Error(String(e)));
    }
  };

  private handleFCP = (metric: FCPMetricWithAttribution) => {
    try {
      const fcpEntry = metric.attribution.fcpEntry;
      this.pushPerformanceAction(this.lastRouteInfo, 'paint', {
        name: 'first-contentful-paint',
        startTime: fcpEntry?.startTime ?? metric.value,
        entryType: 'paint',
        elapsedMs: performanceNow(),
        pageState: this.getPageState(),
        webVitalsSource: true,
      });
      this.debounceFlush();
    } catch (e) {
      captureException(e instanceof Error ? e : new Error(String(e)));
    }
  };

  private handleLoAF = (entry: PerformanceLongAnimationFrameTiming) => {
    try {
      entry.scripts.forEach((script) => {
        if (script.duration >= 50) {
          this.pushPerformanceAction(this.lastRouteInfo, 'longtask', {
            name: script.name,
            entryType: 'longtask',
            startTime: script.startTime,
            duration: script.duration,
            invoker: script.invoker,
            invokerType: script.invokerType,
            pauseDuration: script.pauseDuration,
            executionStart: script.executionStart,
            forcedStyleAndLayoutDuration: script.forcedStyleAndLayoutDuration,
            sourceURL: script.sourceURL,
            sourceFunctionName: script.sourceFunctionName,
            elapsedMs: performanceNow(),
            pageState: this.getPageState(),
            webVitalsSource: true,
          });
          this.debounceFlush();
        }
      });
    } catch (e) {
      captureException(e instanceof Error ? e : new Error(String(e)));
    }
  };

  private constructor(options?: ReporterOptions) {
    super(options);
    this.stopped = false;
    this.lastStatus = undefined;
    this.lastFailureType = undefined;

    const generateTarget = (node: Node | null) =>
      node && 'tagName' in node ? getElementSelector(node) : undefined;

    onLCP(this.handleLCP, { generateTarget, reportSoftNavs: true });
    onINP(this.handleINP, { reportAllChanges: true, generateTarget });
    onCLS(this.handleCLS, { reportAllChanges: false, generateTarget });
    onFCP(this.handleFCP);
    PerformanceEventBus.getInstance().subscribe(
      'long-animation-frame',
      this.handleLoAF
    );

    onVisibilityHidden(() => {
      try {
        this.flushPerformanceQueue();
      } catch (e) {
        captureException(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  report(action: ReporterAction) {
    if (
      action.type === 'COMPONENT_RENDERED' ||
      this.isResolved(action.payload.entry.id) ||
      this.stopped
    ) {
      return;
    }

    switch (action.type) {
      case 'ROUTE_TIMEOUT_EXPIRED': {
        const { routeSpec } = action.payload;
        if (routeSpec) {
          this.lastRouteInfo = { route: routeSpec.route };
        }
        this.lastStatus = 'failure';
        this.lastFailureType = 'watchdogExpired';
        break;
      }
      case 'GLOBAL_ERROR':
      case 'ROUTE_FAILED': {
        const {
          entry: { pathname },
          routeSpec,
        } = action.payload;
        if (routeSpec) {
          this.lastRouteInfo = {
            pathname,
            route: routeSpec.route,
          };
        }
        this.lastStatus = 'failure';
        this.lastFailureType =
          action.type === 'ROUTE_FAILED' ? 'errorSelector' : undefined;
        break;
      }
      case 'ROUTE_PARTIAL_SUCCESS': {
        const {
          entry: { pathname },
          extra: { scenario },
          routeSpec,
        } = action.payload;
        const { route } = routeSpec;
        this.lastRouteInfo = {
          pathname,
          route,
          scenario,
        };
        this.lastStatus = 'partial_success';
        this.lastFailureType = undefined;
        break;
      }
      case 'ROUTE_SUCCEEDED': {
        const {
          entry: { pathname },
          extra: { scenario },
          routeSpec,
        } = action.payload;
        const { route } = routeSpec;
        this.lastRouteInfo = {
          pathname,
          route,
          scenario,
        };
        this.lastStatus = 'success';
        this.lastFailureType = undefined;
        break;
      }
      default:
    }

    switch (action.type) {
      case 'ROUTE_UNEXPECTED': {
        this.stopped = true;
        break;
      }
      case 'GLOBAL_ERROR':
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_SUCCEEDED':
      case 'ROUTE_TIMEOUT_EXPIRED':
      case 'ROUTE_FAILED': {
        this.markResolved(action.payload.entry.id);
        break;
      }
      default:
    }
  }
}
